import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { SERIES_DATA } from "@/data/seriesData";
import api from "@/lib/api";
import { useAuth } from "./AuthContext";

const SeriesDataContext = createContext(null);

/**
 * Merge static SERIES_DATA with per-series admin overrides fetched from backend.
 * - title_override / subtitle_override replace the original.
 * - video_overrides[video_id] patches { title, description, duration } of any video.
 * - extra_videos[] are appended at the end of the series' video list.
 */
function mergeOverrides(overridesByIndex) {
  return SERIES_DATA.map((series) => {
    const ov = overridesByIndex[series.id];
    if (!ov) return series;
    const vOverrides = ov.video_overrides || {};
    const patchedVideos = series.videos.map((v) => {
      const patch = vOverrides[String(v.id)];
      if (!patch) return v;
      return { ...v, ...patch, _titleOverride: !!patch.title };
    });
    const extras = (ov.extra_videos || []).map((v) => ({
      id: v.video_id,
      title: v.title,
      description: v.description || "",
      duration: v.duration || "",
      _extra: true,
      _titleOverride: true,
    }));
    return {
      ...series,
      title: ov.title_override || series.title,
      subtitle: ov.subtitle_override || series.subtitle,
      _titleOverride: !!ov.title_override,
      _subtitleOverride: !!ov.subtitle_override,
      videos: [...patchedVideos, ...extras],
    };
  });
}

export function SeriesDataProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setOverrides({});
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/api/series-overrides");
      const map = {};
      (res.data || []).forEach((o) => { map[o.series_id] = o; });
      setOverrides(map);
    } catch {
      /* silently ignore — keep static SERIES_DATA */
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  const series = mergeOverrides(overrides);

  return (
    <SeriesDataContext.Provider value={{ series, loading, refresh }}>
      {children}
    </SeriesDataContext.Provider>
  );
}

export const useSeriesData = () => {
  const ctx = useContext(SeriesDataContext);
  if (!ctx) throw new Error("useSeriesData must be used inside SeriesDataProvider");
  return ctx;
};
